import {
  ArrowLeft,
  ArrowUpRight,
  CalendarPlus,
  Cake,
  CircleCheck,
  Globe,
  IdCard,
  Info,
  Mail,
  MessageSquareText,
  Phone,
  PhoneCall,
  ShieldCheck,
  TriangleAlert,
  UsersRound,
  X,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Button } from '../../../ui/Button/Button'
import { InternalNotesCard } from '../../conversations/components/InternalNotesCard'
import type { InternalNotesRepository } from '../../conversations/internal-notes-repository'
import type { PatientReminderPreferenceRepository } from '../patient-reminder-preference-repository'
import {
  ageInYears,
  appointmentStatusLabel,
  appointmentStatusTone,
  channelLabel,
  formatBirthDate,
  formatDateTime,
  formatPhone,
  formatPracticeTimestamp,
  languageName,
  patientInitials,
} from '../patient-display'
import type { PatientAddOutcome, PatientListItem } from '../patients-model'
import {
  nextPatientAppointment,
  patientNeedsAttention,
} from '../patients-model'
import styles from '../PatientsPage.module.css'
import {
  PatientReminderPreference,
  REMINDER_PREFERENCE_HINT,
  REMINDER_PREFERENCE_TITLE,
} from './PatientReminderPreference'

function DetailRow({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <div className={styles.detailRow}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

function address(patient: PatientListItem) {
  if (!patient.address) return null
  const locality = [patient.address.city, patient.address.region]
    .filter(Boolean)
    .join(', ')
  return [
    patient.address.line1,
    patient.address.line2,
    [locality, patient.address.postalCode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')
}

function lifecycle(patient: PatientListItem) {
  if (patient.providerLifecycleState === 'ACTIVE')
    return { tone: 'success', label: 'Active NexHealth record' } as const
  if (patient.providerLifecycleState === 'INACTIVE')
    return { tone: 'warning', label: 'Inactive NexHealth record' } as const
  return { tone: 'neutral', label: 'NexHealth status unavailable' } as const
}

function SectionHeader({
  title,
  hint,
  action,
}: {
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <header className={styles.sectionHeader}>
      <div>
        <h2>{title}</h2>
        {hint ? <p>{hint}</p> : null}
      </div>
      {action}
    </header>
  )
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={styles.statusBadge}
      data-tone={appointmentStatusTone(status)}
    >
      {appointmentStatusLabel(status)}
    </span>
  )
}

function AddOutcomeNotice({
  outcome,
  onDismiss,
}: {
  outcome: PatientAddOutcome
  onDismiss: () => void
}) {
  return (
    <div
      className={styles.notice}
      data-tone={outcome === 'CREATED' ? 'success' : 'info'}
      role="status"
    >
      {outcome === 'CREATED' ? (
        <CircleCheck size={16} aria-hidden="true" />
      ) : (
        <Info size={16} aria-hidden="true" />
      )}
      <span>
        {outcome === 'CREATED'
          ? 'Patient added. A new record was created in the practice system through NexHealth.'
          : 'This patient already had a practice record, so Flio opened it instead of creating a duplicate.'}
      </span>
      <button
        type="button"
        className={styles.noticeDismiss}
        aria-label="Dismiss"
        onClick={onDismiss}
      >
        <X size={15} aria-hidden="true" />
      </button>
    </div>
  )
}

export function PatientDetail({
  patient,
  onBack,
  onOpenConversation,
  onAddAppointment,
  tenantId,
  internalNotesRepository,
  reminderPreferenceRepository,
  addOutcome,
}: {
  patient: PatientListItem
  onBack: () => void
  onOpenConversation: () => void
  onAddAppointment: () => void
  tenantId: string
  internalNotesRepository?: InternalNotesRepository
  reminderPreferenceRepository?: PatientReminderPreferenceRepository
  addOutcome?: PatientAddOutcome
}) {
  const [outcome, setOutcome] = useState(addOutcome)
  const next = nextPatientAppointment(patient)
  const history = patient.appointments.filter(
    (appointment) => appointment !== next
  )
  const state = lifecycle(patient)
  const engagement = patient.latestEngagement
  const birthDate = formatBirthDate(patient.dateOfBirth)
  const age = ageInYears(patient.dateOfBirth)
  // Visible words where an icon alone would be ambiguous; the label is read
  // by screen readers only where the value speaks for itself.
  const facts = [
    patient.phone
      ? { icon: Phone, label: 'Phone', value: formatPhone(patient.phone) }
      : null,
    patient.email ? { icon: Mail, label: 'Email', value: patient.email } : null,
    birthDate
      ? {
          icon: Cake,
          label: 'Date of birth',
          value: `Born ${birthDate}${age === null ? '' : ` · ${age} ${age === 1 ? 'yr' : 'yrs'}`}`,
        }
      : null,
    {
      icon: IdCard,
      label: 'Practice patient ID',
      value: `Practice ID ${patient.providerPatientId}`,
    },
  ].filter((fact) => fact !== null)
  const identityAvailable = Boolean(
    patient.middleName ||
    patient.phone ||
    patient.email ||
    patient.dateOfBirth ||
    patient.gender ||
    address(patient)
  )
  return (
    <div className={styles.detailPage}>
      <button type="button" className={styles.backLink} onClick={onBack}>
        <ArrowLeft size={16} aria-hidden="true" /> All patients
      </button>

      {outcome ? (
        <AddOutcomeNotice
          outcome={outcome}
          onDismiss={() => setOutcome(undefined)}
        />
      ) : null}

      <section className={styles.identityCard} aria-labelledby="patient-name">
        <div className={styles.identityMain}>
          <span
            className={`${styles.largeAvatar} ${patient.recordStatus === 'NEW' ? styles.newAvatar : ''}`}
            aria-hidden="true"
          >
            {patientInitials(patient.displayName)}
          </span>
          <div className={styles.identityText}>
            <div className={styles.identityTitle}>
              <h1 id="patient-name">{patient.displayName}</h1>
              {patient.recordStatus === 'NEW' ? (
                <span className={styles.newChip}>New patient</span>
              ) : patient.recordStatus === 'EXISTING' ? (
                <span className={styles.statusChip}>Existing patient</span>
              ) : null}
              <span className={styles.syncChip} data-tone={state.tone}>
                <ShieldCheck size={13} aria-hidden="true" /> {state.label}
              </span>
              {patient.source === 'FLIO_CREATED' ? (
                <span className={styles.statusChip}>Added by Flio</span>
              ) : null}
            </div>
            <ul className={styles.identityFacts} aria-label="Key details">
              {facts.map(({ icon: Icon, label, value }) => (
                <li key={label}>
                  <Icon size={14} aria-hidden="true" />
                  {label === 'Phone' || label === 'Email' ? (
                    <span className={styles.srOnly}>{label}: </span>
                  ) : null}
                  {value}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className={styles.detailActions}>
          <Button type="button" onClick={onAddAppointment}>
            <CalendarPlus size={16} aria-hidden="true" /> Add appointment
          </Button>
        </div>
        {patientNeedsAttention(patient) ? (
          <div className={styles.attentionStrip}>
            <TriangleAlert size={15} aria-hidden="true" />
            <strong>Needs attention</strong>
            <span>Practice-record linkage needs staff review.</span>
          </div>
        ) : null}
      </section>

      <div className={styles.detailGrid}>
        <div className={styles.detailColumn}>
          <section className={styles.detailCard}>
            <SectionHeader
              title="Appointments"
              hint="From the clinic's practice system, through NexHealth"
            />
            <div className={styles.cardBody}>
              {next ? (
                <div className={styles.nextAppointment}>
                  <small>Next appointment</small>
                  <strong>{formatDateTime(next.scheduledAtMs)}</strong>
                  <span>
                    {next.appointmentTypeName ?? 'Appointment'}
                    {next.providerName ? ` · ${next.providerName}` : ''}
                    {next.operatoryName ? ` · ${next.operatoryName}` : ''}
                  </span>
                  <StatusBadge status={next.status} />
                </div>
              ) : (
                <p className={styles.noIndicators}>
                  No upcoming visit is scheduled in the synced appointments.
                </p>
              )}
              {history.length ? (
                <>
                  <h3 className={styles.subheading}>Other appointments</h3>
                  <ol className={styles.appointmentHistory}>
                    {history.map((appointment) => (
                      <li
                        key={appointment.appointmentId}
                        data-tone={appointmentStatusTone(appointment.status)}
                      >
                        <div>
                          <strong>
                            {appointment.appointmentTypeName ?? 'Appointment'}
                          </strong>
                          <span>
                            {formatDateTime(appointment.scheduledAtMs)}
                          </span>
                          {appointment.providerName ? (
                            <span>
                              {appointment.providerName}
                              {appointment.operatoryName
                                ? ` · ${appointment.operatoryName}`
                                : ''}
                            </span>
                          ) : null}
                        </div>
                        <StatusBadge status={appointment.status} />
                      </li>
                    ))}
                  </ol>
                </>
              ) : null}
            </div>
          </section>

          {reminderPreferenceRepository ? (
            <section className={styles.detailCard}>
              <SectionHeader
                title={REMINDER_PREFERENCE_TITLE}
                hint={REMINDER_PREFERENCE_HINT}
              />
              <PatientReminderPreference
                tenantId={tenantId}
                patientAuthorityId={patient.patientAuthorityId}
                displayName={patient.displayName}
                repository={reminderPreferenceRepository}
              />
            </section>
          ) : null}

          <section className={styles.detailCard}>
            <SectionHeader
              title="Recent engagement"
              action={
                patient.interactionAggregateId ? (
                  // The canonical conversation the patient workspace linked
                  // (by aggregate id, never by phone or name); Conversations
                  // opens exactly that one or none.
                  <button
                    type="button"
                    className={styles.sectionAction}
                    onClick={onOpenConversation}
                  >
                    Open in Conversations
                    <ArrowUpRight size={15} aria-hidden="true" />
                  </button>
                ) : undefined
              }
            />
            <div className={styles.cardBody}>
              {engagement ? (
                <div className={styles.engagementItem}>
                  <span className={styles.engagementIcon} aria-hidden="true">
                    {engagement.latestChannel === 'VOICE' ? (
                      <PhoneCall size={15} />
                    ) : engagement.latestChannel === 'WEB_WIDGET' ? (
                      <Globe size={15} />
                    ) : (
                      <MessageSquareText size={15} />
                    )}
                  </span>
                  <div>
                    <span className={styles.engagementMeta}>
                      {channelLabel(engagement.latestChannel)} ·{' '}
                      {formatDateTime(engagement.latestActivityAtMs)}
                    </span>
                    <p className={styles.engagementSummary}>
                      {engagement.latestPreview}
                    </p>
                  </div>
                </div>
              ) : (
                <p className={styles.engagementSummary}>
                  {patient.interactionAggregateId
                    ? 'A Flio conversation is linked; no recent engagement summary is available.'
                    : 'No Flio conversation is linked yet.'}
                </p>
              )}
              {patient.interactionAggregateId &&
              patient.smsContactCapability?.reason === 'SHARED_PHONE' ? (
                <p className={styles.sharedPhoneNote}>
                  <UsersRound size={14} aria-hidden="true" />
                  Other patients share this phone number, so their messages and
                  calls are in the same conversation.
                </p>
              ) : null}
            </div>
          </section>
        </div>

        <div className={styles.detailColumn}>
          <section className={styles.detailCard}>
            <SectionHeader
              title="Patient identity"
              hint="From the practice record"
            />
            <dl className={styles.identityDetails}>
              <DetailRow label="Middle name" value={patient.middleName} />
              <DetailRow label="Phone" value={formatPhone(patient.phone)} />
              <DetailRow label="Email" value={patient.email} />
              <DetailRow label="Date of birth" value={birthDate} />
              <DetailRow label="Gender" value={patient.gender} />
              <DetailRow label="Address" value={address(patient)} />
              {!identityAvailable ? (
                <p className={styles.noIndicators}>
                  Additional patient identity details are unavailable from the
                  connected practice system.
                </p>
              ) : null}
            </dl>
          </section>

          <section className={styles.detailCard}>
            <SectionHeader
              title="Practice record"
              hint="How this patient is linked in NexHealth"
            />
            <dl className={styles.identityDetails}>
              <DetailRow
                label="Record source"
                value={
                  patient.source === 'FLIO_CREATED'
                    ? 'Created by Flio, synced to NexHealth'
                    : 'NexHealth'
                }
              />
              <DetailRow
                label="Primary provider"
                value={patient.primaryProviderName ?? patient.primaryProviderId}
              />
              <DetailRow label="Chart ID" value={patient.chartId} />
              <DetailRow label="PMS patient ID" value={patient.foreignId} />
              <DetailRow
                label="PMS identifier type"
                value={patient.foreignIdType}
              />
              <DetailRow
                label="Preferred language"
                value={languageName(patient.preferredLanguage)}
              />
              <DetailRow label="Billing type" value={patient.billingType} />
              <DetailRow label="Guarantor ID" value={patient.guarantorId} />
              <DetailRow
                label="Practice record created"
                value={formatPracticeTimestamp(patient.providerCreatedAt)}
              />
              <DetailRow
                label="Last practice sync"
                value={formatPracticeTimestamp(patient.providerLastSyncAt)}
              />
            </dl>
            <p className={styles.cardFootnote}>
              Insurance, balances, treatment plans and clinical notes stay in
              the clinic's practice system and aren't shown in Flio.
            </p>
          </section>

          <section className={styles.notesCard}>
            {patient.interactionAggregateId && internalNotesRepository ? (
              <InternalNotesCard
                tenantId={tenantId}
                aggregateId={patient.interactionAggregateId}
                repository={internalNotesRepository}
              />
            ) : (
              <>
                <SectionHeader
                  title="Internal notes"
                  hint="Visible to clinic staff only"
                />
                <div className={styles.cardBody}>
                  <p className={styles.noIndicators}>
                    Notes become available once this patient has a Flio
                    conversation.
                  </p>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
